'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import type { ComponentType } from 'react';
import { Reveal } from '../_components/Reveal';
import {
  IconSend, IconSearch, IconMap, IconPin, IconWrench, IconCheck,
  IconArrowRight, IconBell, IconSparkles,
} from '../_components/icons';

// ─── Typy ─────────────────────────────────────────────────────────────────────

interface PublicTicket {
  ticketNumber: string;
  status: string;
  categoryName: string | null;
  addressText: string | null;
  lat: number | null;
  lng: number | null;
  createdAt: string;
}

interface PublicStats { total: number; open: number; inProgress: number; closed: number }

// ─── Stałe ────────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  NOWE: 'Nowe', WERYFIKACJA: 'Weryfikacja', W_REALIZACJI: 'W realizacji',
  OCZEKUJE_ZEWN: 'Oczekuje', ZAMKNIETE: 'Zamknięte', ODRZUCONE: 'Odrzucone', PRZEKAZANE: 'Przekazane',
};

const STATUS_COLOR: Record<string, { bg: string; text: string; dot: string }> = {
  NOWE:          { bg: '#FFFBEB', text: '#D97706', dot: '#F59E0B' },
  WERYFIKACJA:   { bg: '#EFF6FF', text: '#2563EB', dot: '#3B82F6' },
  W_REALIZACJI:  { bg: '#F5F3FF', text: '#7C3AED', dot: '#8B5CF6' },
  OCZEKUJE_ZEWN: { bg: '#FFF7ED', text: '#EA580C', dot: '#F97316' },
  ZAMKNIETE:     { bg: '#ECFDF5', text: '#059669', dot: '#10B981' },
  ODRZUCONE:     { bg: '#FEF2F2', text: '#DC2626', dot: '#EF4444' },
  PRZEKAZANE:    { bg: '#F0FDF4', text: '#16A34A', dot: '#22C55E' },
};

// ─── Mapa (dynamiczna) ─────────────────────────────────────────────────────────

const PublicMap = dynamic(() => import('./PublicMap'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full bg-sky-50 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-sky-400 border-t-transparent rounded-full animate-spin" />
    </div>
  ),
}) as ComponentType<{ tickets: PublicTicket[] }>;

// ─── Licznik z animacją odliczania ────────────────────────────────────────────

function useCountUp(target: number, durationMs = 1100): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target <= 0) { setValue(0); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / durationMs, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
}

function StatCounter({ value, label, accent, Icon }: {
  value: number; label: string; accent: string; Icon: (p: { className?: string }) => JSX.Element;
}) {
  const shown = useCountUp(value);
  return (
    <div className="flex items-center gap-3.5 px-5 py-1">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${accent}14`, color: accent }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-2xl sm:text-3xl font-extrabold tabular-nums leading-none text-gray-900">
          {shown.toLocaleString('pl-PL')}
        </div>
        <div className="text-xs text-gray-500 mt-1 font-medium">{label}</div>
      </div>
    </div>
  );
}

function StatsBand({ stats }: { stats: PublicStats | null }) {
  if (!stats || stats.total === 0) return null;
  return (
    <div className="max-w-4xl mx-auto w-full px-4 -mt-9 relative z-20">
      <div className="glass rounded-2xl border border-white/60 shadow-card-lg grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-200/70 py-4 rise">
        <StatCounter value={stats.total}      label="Zgłoszeń łącznie" accent="#0EA5E9" Icon={IconPin} />
        <StatCounter value={stats.closed}     label="Rozwiązanych"     accent="#059669" Icon={IconCheck} />
        <StatCounter value={stats.inProgress} label="W realizacji"     accent="#7C3AED" Icon={IconWrench} />
      </div>
    </div>
  );
}

// ─── Wyszukiwarka statusu ──────────────────────────────────────────────────────

function StatusSearch() {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const num = value.trim().toUpperCase();
    if (!num) return;
    setLoading(true);
    setError('');
    try {
      const apiBase = process.env.NEXT_PUBLIC_API_URL ?? '/api';
      const res = await fetch(`${apiBase}/public/tickets/${num}/status`, {
        headers: { Host: window.location.hostname },
      });
      if (!res.ok) { setError('Nie znaleziono zgłoszenia o podanym numerze.'); setLoading(false); return; }
      window.location.href = `/status/${num}`;
    } catch {
      setError('Błąd połączenia — spróbuj ponownie.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSearch} className="w-full">
      <div className="flex flex-col sm:flex-row gap-2.5">
        <div className="relative flex-1">
          <IconSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={value}
            onChange={(e) => { setValue(e.target.value.toUpperCase()); setError(''); }}
            placeholder="np. SZP-2024-00123"
            maxLength={20}
            className="w-full pl-10 pr-4 py-3 text-sm border-2 border-gray-200 bg-white rounded-xl outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-100 transition font-mono placeholder-gray-400 text-gray-800"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !value.trim()}
          className="px-6 py-3 bg-sky-500 text-white rounded-xl text-sm font-bold hover:bg-sky-600 disabled:opacity-50 transition shadow-sm shadow-sky-500/30 whitespace-nowrap"
        >
          {loading ? '…' : 'Sprawdź status'}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">{error}</p>
      )}
    </form>
  );
}

// ─── Strona ─────────────────────────────────────────────────────────────────────

export default function ZgloszeniaPage() {
  const [tickets, setTickets] = useState<PublicTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantName, setTenantName] = useState('Twojej gminie');
  const [stats, setStats] = useState<PublicStats | null>(null);

  const apiBase = process.env.NEXT_PUBLIC_API_URL ?? '/api';

  useEffect(() => {
    const host = window.location.hostname;
    fetch(`${apiBase}/public/tickets/recent?limit=50`, { headers: { Host: host } })
      .then((r) => r.ok ? r.json() : [])
      .catch(() => [])
      .then((t) => setTickets(Array.isArray(t) ? t : []))
      .finally(() => setLoading(false));
    fetch(`${apiBase}/public/branding`, { headers: { Host: host } })
      .then((r) => r.ok ? r.json() : null)
      .then((b) => { if (b?.displayName) setTenantName(b.displayName); })
      .catch(() => {});
    fetch(`${apiBase}/public/tickets/stats`, { headers: { Host: host } })
      .then((r) => r.ok ? r.json() : null)
      .then((s) => { if (s) setStats(s); })
      .catch(() => {});
  }, [apiBase]);

  const mapTickets = tickets.filter((t) => t.lat && t.lng);

  const steps = [
    { img: '/brand/illustrations/step-report.svg', title: 'Zgłoś problem',      desc: 'Wybierz kategorię, zaznacz miejsce na mapie i dodaj zdjęcia. Zajmie Ci to mniej niż 2 minuty.' },
    { img: '/brand/illustrations/step-work.svg',   title: 'Urząd działa',       desc: 'Zgłoszenie trafia do właściwego działu. Na bieżąco śledzisz, co dzieje się z Twoją sprawą.' },
    { img: '/brand/illustrations/step-done.svg',   title: 'Problem rozwiązany', desc: 'Dostajesz powiadomienie e-mail, gdy status się zmieni i usterka zostanie naprawiona.' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">

      {/* ── HEADER ───────────────────────────────────────────────────────────── */}
      <header className="glass sticky top-0 z-40 border-b border-white/50">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 px-4 h-16">
          <div className="flex items-center gap-2.5">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/brand/logo/halogmina-horizontal.svg" alt="HaloGmina.pl" className="h-9 w-auto" />
          </div>
          <nav className="flex items-center gap-2">
            <a href="#status"
              className="hidden sm:inline-flex text-sm font-medium text-gray-600 hover:text-sky-600 px-3 py-2 rounded-lg transition-colors">
              Sprawdź status
            </a>
            <Link href="/report"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 px-4 py-2 rounded-xl transition shadow-sm shadow-sky-500/30">
              <IconSend className="w-4 h-4" /> Zgłoś problem
            </Link>
          </nav>
        </div>
      </header>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Tło: gradient marki (cyan → azure → granat) + aurora + siatka */}
        <div className="absolute inset-0" style={{ background: 'var(--hg-gradient-hero)' }} />
        <div className="blob blob-1 w-[380px] h-[380px] bg-[#22B7E6] -top-20 -left-10" />
        <div className="blob blob-2 w-[420px] h-[420px] bg-[#2AA6DE] top-10 -right-16" />
        <div className="blob blob-3 w-[300px] h-[300px] bg-[#7FCBEC] bottom-0 left-1/3" />
        <div className="absolute inset-0 dot-grid opacity-60" />

        <div className="relative max-w-3xl mx-auto text-center px-4 pt-16 sm:pt-20 pb-24 sm:pb-28">
          <div className="rise inline-flex items-center gap-2 glass-dark text-white px-3.5 py-1.5 rounded-full text-xs font-medium mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-400 live-dot" />
            System aktywny — przyjmujemy zgłoszenia
          </div>
          <h1 className="rise text-4xl sm:text-6xl font-extrabold text-white leading-[1.05] tracking-tight mb-5" style={{ animationDelay: '60ms' }}>
            Zgłoś problem<br className="hidden sm:block" /> w <span className="tenant-name">{tenantName}</span>
          </h1>
          <p className="rise text-sky-50/90 text-base sm:text-lg max-w-xl mx-auto mb-9 leading-relaxed" style={{ animationDelay: '120ms' }}>
            Dziury w jezdni, uszkodzone chodniki, awarie oświetlenia — zgłoś to w kilka chwil
            i śledź postęp naprawy online.
          </p>
          <div className="rise flex flex-col sm:flex-row items-center justify-center gap-3" style={{ animationDelay: '180ms' }}>
            <Link href="/report" aria-label="Zgłoś nowy problem"
              className="shimmer group inline-flex items-center gap-2 bg-white text-sky-700 font-bold px-7 py-4 rounded-2xl hover:shadow-xl transition-all shadow-lg text-base">
              <IconSend className="w-5 h-5" />
              Zgłoś nowy problem
              <IconArrowRight className="w-4 h-4 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" />
            </Link>
            <a href="#mapa"
              className="inline-flex items-center gap-2 glass-dark text-white font-semibold px-6 py-4 rounded-2xl hover:bg-white/20 transition-all text-base">
              <IconMap className="w-5 h-5" /> Zobacz mapę
            </a>
          </div>
          {/* Ilustracja: miasteczko na mapie */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/brand/illustrations/hero-city.svg"
            alt="Ilustracja: zgłoszenia na mapie gminy"
            className="rise w-full max-w-2xl mx-auto mt-12 drop-shadow-2xl"
            style={{ animationDelay: '240ms' }}
          />
        </div>
        {/* Miękkie przejście do sekcji */}
        <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-[#F8FAFC] to-transparent" />
      </section>

      {/* ── PASEK STATYSTYK ──────────────────────────────────────────────────── */}
      <StatsBand stats={stats} />

      <main className="max-w-6xl mx-auto w-full px-4 py-12 flex-1">

        {/* ── MAPA ─────────────────────────────────────────────────────────── */}
        <Reveal>
          <section id="mapa" className="scroll-mt-20">
            <div className="flex items-end justify-between mb-4 gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <IconMap className="w-6 h-6 text-sky-500" /> Mapa zgłoszeń
                </h2>
                <p className="text-sm text-gray-500 mt-1">Zobacz, co zgłaszają mieszkańcy w Twojej okolicy.</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-card overflow-hidden">
              <div className="px-4 pt-3.5 pb-2.5 flex items-center flex-wrap gap-2 border-b border-gray-100">
                {Object.entries(STATUS_COLOR).filter(([s]) => !['ODRZUCONE'].includes(s)).map(([status, sc]) => (
                  <span key={status} className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full"
                    style={{ background: sc.bg, color: sc.text }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: sc.dot }} />
                    {STATUS_LABELS[status]}
                  </span>
                ))}
              </div>
              <div className="h-[460px] sm:h-[540px]">
                {!loading && <PublicMap tickets={mapTickets} />}
                {loading && (
                  <div className="h-full bg-sky-50 flex items-center justify-center">
                    <div className="w-8 h-8 border-4 border-sky-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            </div>
          </section>
        </Reveal>

        {/* ── JAK TO DZIAŁA ─────────────────────────────────────────────────── */}
        <Reveal>
          <section className="mt-16">
            <div className="text-center max-w-xl mx-auto mb-10">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-600 bg-sky-50 px-3 py-1 rounded-full mb-3">
                <IconSparkles className="w-3.5 h-3.5" /> Proste jak nigdy
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Jak to działa?</h2>
            </div>
            <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-5">
              {/* linia łącząca (desktop) */}
              <div className="hidden sm:block absolute top-24 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-[#BEE4F6] via-[#7FCBEC] to-[#FFD666]" />
              {steps.map(({ img, title, desc }, i) => (
                <div key={i} className="relative bg-white rounded-2xl border border-gray-200 p-6 text-center shadow-card transition-all hover:-translate-y-1 hover:shadow-card-lg">
                  <div className="relative w-32 h-32 mx-auto mb-3">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={img} alt="" className="w-32 h-32" />
                    <div className="absolute top-1 right-3 w-7 h-7 rounded-full bg-white border-2 border-sky-100 text-sky-600 text-sm font-bold flex items-center justify-center shadow-sm">
                      {i + 1}
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ── SPRAWDŹ STATUS ───────────────────────────────────────────────── */}
        <Reveal>
          <section id="status" className="mt-16 scroll-mt-20">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-card p-6 sm:p-8 max-w-2xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
                  <IconSearch className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Masz już numer zgłoszenia?</h3>
                  <p className="text-sm text-gray-500">Sprawdź, na jakim etapie jest Twoja sprawa.</p>
                </div>
              </div>
              <StatusSearch />
            </div>
          </section>
        </Reveal>

        {/* ── CTA ──────────────────────────────────────────────────────────── */}
        <Reveal>
          <section className="mt-16 relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500 via-sky-600 to-indigo-600" />
            <div className="blob blob-2 w-[300px] h-[300px] bg-cyan-300 -top-16 -right-10 opacity-40" />
            <div className="absolute inset-0 dot-grid opacity-50" />
            <div className="relative px-6 py-12 sm:py-14 text-center text-white">
              <IconBell className="w-8 h-8 mx-auto mb-3 opacity-90" />
              <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">Widzisz problem w gminie?</h2>
              <p className="text-sky-50/90 text-sm sm:text-base mb-7 max-w-md mx-auto">
                Nie czekaj — Twoje zgłoszenie realnie pomaga urzędowi szybciej reagować.
              </p>
              <Link href="/report"
                className="shimmer inline-flex items-center gap-2 bg-white text-sky-700 font-bold px-7 py-3.5 rounded-2xl hover:shadow-xl transition-all shadow-lg">
                <IconSend className="w-5 h-5" /> Zgłoś problem teraz
              </Link>
            </div>
          </section>
        </Reveal>
      </main>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer className="bg-white border-t border-gray-100 py-7 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-400">
          <div className="flex items-center gap-2.5">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/brand/logo/halogmina-horizontal.svg" alt="HaloGmina.pl" className="h-8 w-auto opacity-90" />
            <span>© {new Date().getFullYear()} HaloGmina.pl — zgłoszenia publiczne</span>
          </div>
          <div className="flex items-center gap-4">
            <Link href="/report" className="hover:text-sky-500 transition">Nowe zgłoszenie</Link>
            <span className="text-gray-200">•</span>
            <Link href="/panel" className="hover:text-sky-500 transition">Panel urzędnika</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
